// Private provider compatibility boundary. This is the only homepage asset
// allowed to inspect provider runtime data; callers receive normalized,
// fail-closed availability, eligibility and variant-id state.
//
// Homepage promotion contract:
// 1. Find exactly one enabled Kite offer by the configured offer ID.
// 2. Let Kite decide campaign/customer eligibility and the Buy X condition.
// 3. Expose only Y variant IDs that match both Kite and this Section's products.
// 4. Publish one state: unavailable, locked or unlocked. Unknown means unavailable.
// The theme never duplicates X Collection, quantity, price or usage-limit rules.
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document && !root.CozyNookKiteOfferAdapter) root.CozyNookKiteOfferAdapter = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const STATES = Object.freeze({ unavailable: 'unavailable', locked: 'locked', unlocked: 'unlocked' });

  function numericId(value) {
    const match = String(value && (value.id || value.collectionId || value.gid) || value || '').match(/(\d+)$/);
    const id = match ? Number(match[1]) : NaN;
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  }

  function normalizedTag(value) {
    return String(value || '').trim().toLocaleLowerCase();
  }

  // A Tag/Collection rule identifies products, not variants. The Section
  // supplies its server-rendered variants, and every candidate must satisfy the
  // configured source before any ID is exposed to the promotion controller.
  function candidateVariantIds(candidates, requiredTag) {
    if (!Array.isArray(candidates) || candidates.length === 0) return [];
    const expectedTag = normalizedTag(requiredTag);
    const ids = new Set();
    const valid = candidates.every(function (candidate) {
      const productId = numericId(candidate && candidate.productId);
      const tags = candidate && candidate.tags;
      const variants = candidate && candidate.variantIds;
      if (!productId || !Array.isArray(tags) || !Array.isArray(variants) || variants.length === 0) return false;
      if (expectedTag && !tags.some(function (tag) { return normalizedTag(tag) === expectedTag; })) return false;
      return variants.every(function (variantId) {
        const id = numericId(variantId);
        if (!id) return false;
        ids.add(id);
        return true;
      });
    });
    return valid && ids.size > 0 ? Array.from(ids).sort(function (left, right) { return left - right; }) : [];
  }

  // Accept only the single-segment/single-Y-rule shapes verified in this store.
  // Unknown, excluded, mismatched or malformed rules return no eligible IDs.
  function extractEligibleVariantIds(rawOffer, options) {
    const offer = rawOffer && rawOffer.source ? rawOffer.source : rawOffer;
    const segments = offer && offer.segmentsData && offer.segmentsData.segmentsList;
    if (!Array.isArray(segments) || segments.length !== 1) return [];
    const rules = segments[0] && segments[0].getYRulesData && segments[0].getYRulesData.rulesList;
    if (!Array.isArray(rules) || rules.length !== 1) return [];
    const rule = rules[0];
    const ruleValue = rule && rule.ruleValue;
    const values = ruleValue && ruleValue.value;
    if (!rule || !Array.isArray(values) || values.length === 0 || ruleValue.operatorType === 'exclude') return [];

    if (rule.ruleType === 'productTags') {
      const expectedTag = String(options && options.productTag || '').trim();
      if (String(options && options.yRuleType || '') !== 'product_tag' || values.length !== 1 || normalizedTag(values[0]) !== normalizedTag(expectedTag)) return [];
      return candidateVariantIds(options && options.candidateProducts, expectedTag);
    }

    if (rule.ruleType === 'collections') {
      const expectedCollectionId = numericId(options && options.collectionId);
      if (String(options && options.yRuleType || '') !== 'collection' || values.length !== 1 || numericId(values[0]) !== expectedCollectionId) return [];
      return candidateVariantIds(options && options.candidateProducts, '');
    }

    if (rule.ruleType !== 'products' && rule.ruleType !== 'productVariants') return [];
    const ids = new Set();
    const valid = values.every(function (product) {
      const variants = product && product.variants;
      if (!Array.isArray(variants) || variants.length === 0) return false;
      return variants.every(function (variant) {
        const id = numericId(variant && variant.variantId);
        if (!id) return false;
        ids.add(id);
        return true;
      });
    });
    return valid && ids.size > 0 ? Array.from(ids).sort(function (left, right) { return left - right; }) : [];
  }

  function interpretVisibility(result) {
    if (!result || !Array.isArray(result.checkpoints)) return STATES.unavailable;
    const checkpoints = Object.create(null);
    result.checkpoints.forEach(function (checkpoint) {
      if (checkpoint && typeof checkpoint.name === 'string' && typeof checkpoint.passed === 'boolean') {
        checkpoints[checkpoint.name] = checkpoint.passed;
      }
    });
    if (
      typeof checkpoints.campaign !== 'boolean' ||
      typeof checkpoints.customerEligibility !== 'boolean' ||
      typeof checkpoints.discountTriggeredRules !== 'boolean'
    ) return STATES.unavailable;
    if (!checkpoints.campaign || !checkpoints.customerEligibility) return STATES.unavailable;
    return checkpoints.discountTriggeredRules ? STATES.unlocked : STATES.locked;
  }

  function mergeCartProductData(cart, runtimeCart) {
    if (!cart || !Array.isArray(cart.items)) return cart;
    const cached = new Map();
    const runtimeItems = runtimeCart && Array.isArray(runtimeCart.items) ? runtimeCart.items : [];
    runtimeItems.forEach(function (item) {
      if (item && item.variant_id) cached.set(String(item.variant_id), item);
    });
    return Object.assign({}, cart, {
      items: cart.items.map(function (item) {
        const source = item && cached.get(String(item.variant_id));
        if (!source) return item;
        return Object.assign({}, item, {
          productData: item.productData || source.productData,
          latestShopifyData: item.latestShopifyData || source.latestShopifyData,
        });
      }),
    });
  }

  // Keep all provider-global access inside this runtime wrapper so the visual
  // controller never depends directly on private object names or methods.
  function createBrowserRuntime(options) {
    const target = options.window || window;
    const doc = options.document || target.document;
    const timers = options.timers || target;
    const listeners = new Set();
    let pollTimer = null;
    let stopTimer = null;
    let stopped = false;
    let gateSequence = 0;

    function parts() {
      const kite = target.gfg;
      const offers = kite && kite.settings && kite.settings.consolidatedCustomDiscount;
      const engine = kite && (kite.ruleEngine || kite.rules);
      if (!kite || !kite.state || kite.state.bootstrapComplete !== true || !Array.isArray(offers) || !engine) return null;
      const buyX = target.gfgCustomDiscount && target.gfgCustomDiscount.gfgBXGY;
      const dynamicBogo = target.gfgConsolidatedCustomDiscount &&
        target.gfgConsolidatedCustomDiscount.gfgDynamicBogo &&
        target.gfgConsolidatedCustomDiscount.gfgDynamicBogo.f;
      return { kite: kite, offers: offers, engine: engine, buyX: buyX, dynamicBogo: dynamicBogo };
    }

    function notify() {
      listeners.forEach(function (callback) { callback(); });
    }

    function targetOfferReady() {
      try {
        const runtime = parts();
        const targetId = String(options.offerId || '').trim();
        return Boolean(
          runtime &&
          typeof runtime.engine.evaluateWidgetVisibility === 'function' &&
          runtime.buyX &&
          typeof runtime.buyX.computeWidgetState === 'function' &&
          runtime.buyX.state &&
          runtime.dynamicBogo &&
          typeof runtime.dynamicBogo.convertCCDSchemaToDynamicBogoSchema === 'function' &&
          targetId &&
          runtime.offers.some(function (offer) {
          return offer && offer.isEnabled === true && offer.isDeleted !== true && String(offer._id || '') === targetId;
          })
        );
      } catch (_error) {
        return false;
      }
    }

    function schedulePoll() {
      if (stopped || pollTimer) return;
      pollTimer = timers.setTimeout(function () {
        pollTimer = null;
        notify();
        if (targetOfferReady()) {
          stopped = true;
          if (stopTimer) timers.clearTimeout(stopTimer);
          stopTimer = null;
        } else {
          schedulePoll();
        }
      }, 250);
    }

    function moduleLoaded() { notify(); }
    if (doc) doc.addEventListener('gfg:module:loaded', moduleLoaded);
    schedulePoll();
    stopTimer = timers.setTimeout(function () {
      stopped = true;
      if (pollTimer) timers.clearTimeout(pollTimer);
      pollTimer = null;
    }, 6000);

    return {
      listOffers: function () {
        const runtime = parts();
        if (!runtime) return [];
        return runtime.offers
          .filter(function (offer) { return offer && offer.isEnabled === true && offer.isDeleted !== true; })
          .map(function (source) { return { id: String(source._id || ''), source: source }; });
      },
      evaluateOffer: function (offer, cart) {
        const runtime = parts();
        if (!runtime || !cart || typeof runtime.engine.evaluateWidgetVisibility !== 'function') return STATES.unavailable;
        const effectiveCart = mergeCartProductData(cart, runtime.kite.state.cartData);
        return interpretVisibility(runtime.engine.evaluateWidgetVisibility(offer.source, null, { cart: effectiveCart }));
      },
      // Kite calculates Buy X separately from its general visibility rules.
      // Run that exact calculation against the controller's latest cart under
      // an isolated state key, so this Section never changes Kite's own widget.
      evaluateBuyX: async function (offer, cart) {
        const runtime = parts();
        if (
          !runtime ||
          !runtime.buyX ||
          typeof runtime.buyX.computeWidgetState !== 'function' ||
          !runtime.buyX.state ||
          !runtime.dynamicBogo ||
          typeof runtime.dynamicBogo.convertCCDSchemaToDynamicBogoSchema !== 'function'
        ) return null;
        let gateTitle = '';
        try {
          const convertedOffer = await runtime.dynamicBogo.convertCCDSchemaToDynamicBogoSchema(offer.source);
          if (!convertedOffer || !cart || !Array.isArray(cart.items)) return null;
          gateTitle = String(convertedOffer.title || 'offer') + '__homepage_gate_' + (++gateSequence);
          const gateOffer = Object.assign({}, convertedOffer, { title: gateTitle });
          const effectiveCart = mergeCartProductData(cart, runtime.kite.state.cartData);
          await runtime.buyX.computeWidgetState(gateOffer, effectiveCart);
          const passed = runtime.buyX.state.BuyXConditionFulfilled &&
            runtime.buyX.state.BuyXConditionFulfilled[gateTitle];
          return passed === true ? true : passed === false ? false : null;
        } catch (_error) {
          return null;
        } finally {
          if (gateTitle) {
            ['BuyXConditionFulfilled', 'allowedGetYQuantity', 'isBuyXGetX', 'buyXGetXCartProductIds', 'buyXVariantData'].forEach(function (key) {
              if (runtime.buyX.state[key]) delete runtime.buyX.state[key][gateTitle];
            });
          }
        }
      },
      observe: function (callback) {
        listeners.add(callback);
        return function () { listeners.delete(callback); };
      },
      destroy: function () {
        stopped = true;
        listeners.clear();
        if (pollTimer) timers.clearTimeout(pollTimer);
        if (stopTimer) timers.clearTimeout(stopTimer);
        if (doc) doc.removeEventListener('gfg:module:loaded', moduleLoaded);
      },
    };
  }

  // Public adapter contract consumed by the promotion controller.
  function create(options) {
    const offerId = String(options && options.offerId || '').trim();
    if (!offerId) throw new Error('offerId is required');
    const runtime = options.runtime || createBrowserRuntime(options || {});
    const subscribers = new Set();
    let state = STATES.unavailable;
    let eligibleVariantIds = [];
    let eligibleSignature = '';
    let latestCart = null;
    let destroyed = false;
    let evaluationGeneration = 0;

    function publish(nextState, nextEligibleVariantIds) {
      if (destroyed) return state;
      const ids = Array.isArray(nextEligibleVariantIds) ? nextEligibleVariantIds.slice() : [];
      const signature = ids.join('|');
      if (nextState === state && signature === eligibleSignature) return state;
      state = nextState;
      eligibleVariantIds = ids;
      eligibleSignature = signature;
      subscribers.forEach(function (callback) { callback(state); });
      return state;
    }

    function refresh(cart) {
      if (destroyed) return STATES.unavailable;
      const generation = ++evaluationGeneration;
      try {
        if (cart && Array.isArray(cart.items)) latestCart = cart;
        const matches = runtime.listOffers().filter(function (offer) { return offer.id === offerId; });
        if (matches.length !== 1) return publish(STATES.unavailable, []);
        const ids = extractEligibleVariantIds(matches[0], options);
        if (ids.length === 0) return publish(STATES.unavailable, []);

        const visibilityState = runtime.evaluateOffer(matches[0], latestCart);
        if (visibilityState !== STATES.unlocked) return publish(visibilityState, ids);
        if (typeof runtime.evaluateBuyX !== 'function') return publish(STATES.unavailable, ids);

        // Close Add immediately while Kite checks the current cart. The
        // generation guard prevents an older async result from reopening Add
        // after a newer cart state has already been evaluated.
        publish(STATES.locked, ids);
        Promise.resolve(runtime.evaluateBuyX(matches[0], latestCart)).then(function (passed) {
          if (destroyed || generation !== evaluationGeneration) return;
          publish(passed === true ? STATES.unlocked : passed === false ? STATES.locked : STATES.unavailable, ids);
        }, function () {
          if (destroyed || generation !== evaluationGeneration) return;
          publish(STATES.unavailable, ids);
        });
        return state;
      } catch (_error) {
        return publish(STATES.unavailable, []);
      }
    }

    const stopObserve = typeof runtime.observe === 'function'
      ? runtime.observe(function () { refresh(latestCart); })
      : function () {};

    return Object.freeze({
      getState: function () { return state; },
      getEligibleVariantIds: function () { return eligibleVariantIds.slice(); },
      refresh: refresh,
      subscribe: function (callback) {
        if (destroyed || typeof callback !== 'function') return function () {};
        subscribers.add(callback);
        callback(state);
        return function () { subscribers.delete(callback); };
      },
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        evaluationGeneration += 1;
        subscribers.clear();
        stopObserve();
        if (typeof runtime.destroy === 'function') runtime.destroy();
      },
    });
  }

  return Object.freeze({ version: 2, STATES: STATES, create: create, createBrowserRuntime: createBrowserRuntime, interpretVisibility: interpretVisibility, mergeCartProductData: mergeCartProductData, extractEligibleVariantIds: extractEligibleVariantIds });
});
